import { v4 as uuidv4 } from "uuid";

import { getSpecialUnits, onDeleteSpecialUnitsList, onSpecialUnitsListModification } from "@Motivators/api/specialUnits";
import { getMyTemporaryGuaranteed } from "@Motivators/api/temporatyGuaranteed";
import { getMyTraining } from "@Motivators/api/training";

//Subscriptions
import {
	getAdministrationToolsOpportunityMining,
	getAutoAlertsItems,
	onAddAutoAlertItem,
	onDeleteAutoAlertItems,
	onUpdateAutoAlertItem,
} from "@Motivators/api/autoAlerts";

import {
	onDeleteSpiffItems,
	onDeleteSpiffSetup,
	onUpdateSpiffItem,
} from "@Motivators/api/spiffs";
import { getSpiffAwards } from "@/Motivators/api/awards";
import { onCreateAward, onDeleteAward, onUpdateAward } from "@/Motivators/api/spiffs/subscriptions";

const getMyMotivators = (motivatorType = "Sales") => {
	const isService = motivatorType === "Service";
	const incentives = {
		firstMotivator: isService ? "spiffsBonuses" : "spiffs",
		secondMotivator: isService ? "training" : "specialUnits",
		thirdMotivator: isService ? "temporaryGuaranteed" : "autoAlerts",
	};
	const queries = {
		getFirstMotivator: async (args) => {
			const response = await getSpiffAwards({ ...args, recipient: args?.recipientId });
			return response.flatMap((item) => {
				return (item.awards || []).map((award) => {
					const cars = isService
						? award.repairOrders
								?.map((order) => order?.vinNumber)
								.filter(Boolean)
								.join(", ")
						: award.deals
								?.map((deal) => deal?.stockNumber)
								.filter(Boolean)
								.join(", ");
					return {
						...award,
						title: item.spiffSetup?.title,
						eligibility: item.spiffItem?.eligibility,
						cars,
						requiredDealStatus: item.spiffItem?.requiredDealStatus,
						createdAt: award.createdAt,
					};
				});
			});
		},
		getSecondMotivator: null,
		getThirdMotivator: null,
		getProviderName: null,
	};

	if (isService) {
		return serviceMotivator({ incentives, queries });
	} else {
		return salesMotivator({ incentives, queries });
	}
};

const getMyMotivatorsAddSubscription = (commissionCycleId, motivatorType = "Sales") => {
	if (motivatorType === "Service") {
		return serviceMotivatorAddSubscription();
	} else {
		return salesMotivatorAddSubscription(commissionCycleId);
	}
};

const serviceMotivator = ({ incentives, queries }) => {
	queries.getSecondMotivator = async (args) => {
		const { locationId, commissionCycleId, userId } = args;
		const response = await getMyTraining(locationId, commissionCycleId, userId);
		return response.map((item) => {
			return {
				...item,
				createdBy: item.createdBy.fullName,
			};
		});
	};
	queries.getThirdMotivator = async (...args) => {
		const [locationId, userId, commissionCycleId] = args;
		const response = await getMyTemporaryGuaranteed({ locationId, commissionCycleId, userId });
		return response.map((item) => {
			return {
				...item,
				addedBy: item.addedBy.fullName,
			};
		});
	};
	queries.getProviderName = async (args) => {
		const { title } = args;
		return title;
	};

	return { incentives, queries };
};

const salesMotivator = ({ incentives, queries }) => {
	queries.getSecondMotivator = async (args) => {
		const { locationId, commissionCycleId, employeeId } = args;
		return getSpecialUnits(locationId, commissionCycleId, { employeeId });
	};
	queries.getThirdMotivator = async (...args) => {
		const [locationId, userId, commissionCycledId] = args;
		return getAutoAlertsItems(locationId, commissionCycledId, { sellerId: userId });
	};
	queries.getProviderName = async (args) => {
		const { locationId, title } = args;
		const response = await getAdministrationToolsOpportunityMining(locationId);

		if (response?.integrations?.opportunityMining?.provider) {
			const firstProvider = response.integrations.opportunityMining.provider.shift();
			return firstProvider?.providerName ? `${title} (${firstProvider?.providerName})` : title;
		}

		return title;
	};

	const subscriptions = {
		// Spiffs
		onCreateAward: salesMotivatorCreateAward,
		onDeleteAward: salesMotivatorDeleteAward,
		onUpdateAward: salesMotivatorUpdateAward,
		onUpdateSpiffItem: salesMotivatorUpdateSpiffItem,
		onDeleteSpiffSetup: salesMotivatorDeleteSpiffSetup,
		onDeleteSpiffItems: salesMotivatorDeleteSpiffItems,
		// Special Units
		onSpecialUnitsListModification: salesMotivatorSpecialUnitsListModification,
		onDeleteSpecialUnitsList: salesMotivatorDeleteSpecialUnitsList,
		// Auto Alerts
		onAddAutoAlertItem: salesMotivatorAddAutoAlertItem,
		onDeleteAutoAlertItem: salesMotivatorDeleteAutoAlertItem,
		onUpdateAutoAlertItem: salesMotivatorUpdateAutoAlertItem,
	};

	return { incentives, queries, subscriptions };
};

const salesMotivatorCreateAward = ({ res, queryClient, locationId, currentUserId }) => {
	const commissionCycleId = res?.data?.onCreateAward?.commissionCycleId;
	const newItem = res?.data?.onCreateAward?.definitions[0];
	if (currentUserId !== newItem?.awards[0]?.recipient?._id) {
		return;
	}
	queryClient.setQueryData(["myIncentivesFirstMotivator", locationId, commissionCycleId, currentUserId], (oldData) => {
		return oldData?.length
			? [
					...oldData,
					{
						id: uuidv4(),
						_id: newItem?.awards[0]?._id,
						eligibility: newItem?.eligibility,
						reward: newItem?.reward,
						createdAt: newItem?.awards[0]?.createdAt,
						title: newItem?.title,
					},
			  ]
			: [
					{
						id: uuidv4(),
						_id: newItem?.awards[0]?._id,
						eligibility: newItem?.eligibility,
						reward: newItem?.reward,
						createdAt: newItem?.awards[0]?.createdAt,
						title: newItem?.title,
					},
			  ];
	});
};

const salesMotivatorDeleteAward = ({ res, queryClient, locationId, currentUserId }) => {
	const commissionCycleId = res?.data?.onDeleteAward?.commissionCycleId;
	const rewards = res?.data?.onDeleteAward?.definitions[0]?.awards;
	const userIsIncluded = rewards.filter((item) => item.recipient._id === currentUserId);
	if (!userIsIncluded.length) {
		return;
	}
	const rewardsId = userIsIncluded.map((item) => item._id);
	queryClient.setQueryData(["myIncentivesFirstMotivator", locationId, commissionCycleId, currentUserId], (oldData) =>
		oldData?.filter((item) => !rewardsId.includes(item._id))
	);
};

const salesMotivatorUpdateAward = ({ res, queryClient, locationId, currentUserId }) => {
	const commissionCycleId = res?.data?.onUpdateAward?.commissionCycleId;
	const definition = res?.data?.onUpdateAward?.definitions[0];
	const updatedAward = definition?.awards[0];
	queryClient.setQueryData(["myIncentivesFirstMotivator", locationId, commissionCycleId, currentUserId], (oldData) => {
		const awardInCache = oldData?.length ? oldData.some((item) => item._id === updatedAward._id) : false;
		if (updatedAward.recipient._id !== currentUserId && !awardInCache) {
			return oldData;
		}
		if (updatedAward.recipient._id !== currentUserId && awardInCache) {
			return oldData.filter((item) => item._id !== updatedAward._id);
		}
		if (updatedAward.recipient._id === currentUserId && !awardInCache) {
			updatedAward.id = uuidv4();
			updatedAward.eligibility = definition.eligibility;
			updatedAward.reward = definition.reward;
			updatedAward.title = definition.title;
			return oldData?.length ? [...oldData, updatedAward] : [updatedAward];
		}
		return oldData;
	});
};

const salesMotivatorUpdateSpiffItem = () => {
	//TODO: Implement this function
};

const salesMotivatorDeleteSpiffSetup = () => {
	//TODO: Implement this function
};

const salesMotivatorDeleteSpiffItems = () => {
	//TODO: Implement this function
};

const salesMotivatorSpecialUnitsListModification = ({ res, queryClient, locationId, employeeId }) => {
	const commissionCycleId = res?.data?.onSpecialUnitsListModification?.commissionCycleId;
	queryClient.refetchQueries({ queryKey: ["myIncentivesSecondMotivator", locationId, employeeId, commissionCycleId] });
};

const salesMotivatorDeleteSpecialUnitsList = ({ res, queryClient, locationId, employeeId }) => {
	const commissionCycleId = res?.data?.onDeleteSpecialUnitsList?.commissionCycleId;
	queryClient.refetchQueries({ queryKey: ["myIncentivesSecondMotivator", locationId, employeeId, commissionCycleId] });
};

const salesMotivatorAddAutoAlertItem = ({ res, queryClient, locationId, currentUserId }) => {
	const commissionCycleId = res?.data?.onAddAutoAlertItem?.commissionCycleId;
	const newItem = res?.data?.onAddAutoAlertItem?.items[0];
	queryClient.setQueryData(["myIncentivesThirdMotivator", locationId, commissionCycleId, currentUserId], (oldData) => {
		if (currentUserId !== newItem?.seller?._id) {
			return oldData;
		}
		newItem.id = uuidv4();
		return oldData?.length ? [...oldData, newItem] : [newItem];
	});
};

const salesMotivatorDeleteAutoAlertItem = ({ res, queryClient, locationId, currentUserId }) => {
	const commissionCycleId = res?.data?.onDeleteAutoAlertItems?.commissionCycleId;
	const itemsDeleted = res?.data?.onDeleteAutoAlertItems?.items?.map((items) => items._id);
	queryClient.setQueryData(["myIncentivesThirdMotivator", locationId, commissionCycleId, currentUserId], (oldData) =>
		oldData.filter((item) => !itemsDeleted.includes(item._id))
	);
};

const salesMotivatorUpdateAutoAlertItem = ({ res, queryClient, locationId, currentUserId }) => {
	const commissionCycleId = res?.data?.onUpdateAutoAlertItem?.commissionCycleId;
	const itemsUpdated = res?.data?.onUpdateAutoAlertItem?.items[0];
	queryClient.setQueryData(["myIncentivesThirdMotivator", locationId, commissionCycleId, currentUserId], (oldData) => {
		if (itemsUpdated.seller._id !== currentUserId) {
			return oldData?.filter((item) => item._id !== itemsUpdated._id);
		}

		const newItem = oldData?.length ? oldData?.every((item) => item._id !== itemsUpdated._id) : true;
		if (newItem) {
			itemsUpdated.id = uuidv4();
			return oldData?.length ? [...oldData, itemsUpdated] : [itemsUpdated];
		}

		return oldData?.map((item) => {
			if (item._id !== itemsUpdated._id) {
				return item;
			}
			return {
				...item,
				...itemsUpdated,
			};
		});
	});
};

const salesMotivatorAddSubscription = (commissionCycleId) => {
	return [
		{
			context: "onCreateAward",
			query: onCreateAward,
			variables: {
				_id: commissionCycleId,
			},
		},
		{
			context: "onDeleteAward",
			query: onDeleteAward,
			variables: {
				_id: commissionCycleId,
			},
		},
		{
			context: "onUpdateAward",
			query: onUpdateAward,
			variables: {
				_id: commissionCycleId,
			},
		},
		{
			context: "onUpdateSpiffItem",
			query: onUpdateSpiffItem,
			variables: {
				_id: commissionCycleId,
			},
		},
		{
			context: "onDeleteSpiffSetup",
			query: onDeleteSpiffSetup,
			variables: {
				_id: commissionCycleId,
			},
		},
		{
			context: "onDeleteSpiffItems",
			query: onDeleteSpiffItems,
			variables: {
				_id: commissionCycleId,
			},
		},
		{
			context: "onSpecialUnitsListModification",
			query: onSpecialUnitsListModification,
			variables: {
				commissionCycleId,
			},
		},
		{
			context: "onDeleteSpecialUnitsList",
			query: onDeleteSpecialUnitsList,
			variables: {
				commissionCycleId,
			},
		},
		{
			context: "onAddAutoAlertItem",
			query: onAddAutoAlertItem,
			variables: {
				_id: commissionCycleId,
			},
		},
		{
			context: "onDeleteAutoAlertItem",
			query: onDeleteAutoAlertItems,
			variables: {
				_id: commissionCycleId,
			},
		},
		{
			context: "onUpdateAutoAlertItem",
			query: onUpdateAutoAlertItem,
			variables: {
				_id: commissionCycleId,
			},
		},
	];
};

//TODO: Implement the subscriptions for the service motivator
const serviceMotivatorAddSubscription = () => [];

export { getMyMotivators, getMyMotivatorsAddSubscription };
